import { useNavigate, useParams } from 'react-router-dom'
import { Badge, EmptyState } from '@cinesend/ui-kit'
import DataTable from '../data_table'
import RowMenu from '../row_menu'
import useStore from '../../hooks/use_store'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import useTableFilters from '../../hooks/use_table_filters'
import { convertToShorthand } from '../../helpers/convert_date'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'

const TYPE_LABELS = {
  'warning-slates': 'Slate Sets',
  'ad-pools': 'Ad Sets',
}

const TYPE_PATHS = {
  'warning-slates': 'slate-sets',
  'ad-pools': 'ad-sets',
}

const GroupsPoolsTable = ({ queryKey, categoryType }) => {
  const params = useParams()
  const { deleteModel } = useStore()
  const queryClient = useQueryClient()
  const { data: airline, error: airlineError, refetch: refetchAirline } = useQuery(['airlines/', params.airlineID])
  const label = TYPE_LABELS[categoryType]
  const isAdPool = categoryType === 'ad-pools'

  const { queryString, searching, sorting, filtering } = useTableFilters({
    filteringOptions: null,
    enableSearch: true,
    queryExists: true,
    persistKey: `groups-pools-table-${params.cycleID}-${categoryType}`,
  })

  const queryWithTypeParam = `${queryString}&type=${categoryType}`

  const { data: categories, isLoading, error, refetch } = useQuery([queryKey, queryWithTypeParam])

  const navigate = useNavigate()

  const columns = [
    {
      key: 'title',
      header: 'Title',
      render: (data) => (isAdPool ? data.external_unique_name || MISSING_UNIQUE_NAME : data.default_title || ''),
    },
    {
      key: 'assigned_assets',
      header: 'Assigned Assets',
      render: (data) => (
        <div className='space-x-2'>
          {(data.assigned_assets ?? []).map((item, index) => (
            <Badge key={`${data.id}-${item?.title}-${index}`}>{item?.title}</Badge>
          ))}
        </div>
      ),
    },
    { key: 'count', header: 'Count', render: (data) => (data.assigned_assets ?? []).length },
    ...(isAdPool
      ? [
          {
            key: 'start_date',
            header: 'Start Date',
            render: (data) => convertToShorthand(data.start_date),
          },
          { key: 'end_date', header: 'End Date', render: (data) => convertToShorthand(data.end_date) },
        ]
      : []),
    {
      key: 'actions',
      header: '',
      width: '65px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.default_title}`}
          items={[
            {
              id: 'delete',
              label: 'Delete',
              tone: 'danger',
              onSelect: () =>
                deleteModel.mutate(
                  {
                    endpoint: 'categories',
                    id: data.id,
                    deleteNoun: categoryType === 'ad-pools' ? 'ad set' : 'slate set',
                    deleteName: isAdPool ? data.external_unique_name || data.default_title : data.default_title,
                  },
                  {
                    onSuccess: () => {
                      queryClient.invalidateQueries([queryKey])
                    },
                  }
                ),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <DataTable
      caption={label}
      columns={columns}
      rows={categories ?? []}
      onRowClick={(data) =>
        navigate(
          `/airlines/${params.airlineID}/cycles/${params.cycleID}/ads/${TYPE_PATHS[categoryType]}/${data.id}/details`
        )
      }
      isLoading={isLoading || !airline}
      error={error || airlineError}
      onRetry={() => {
        refetch()
        refetchAirline()
      }}
      empty={
        <EmptyState
          title={`No ${label} found!`}
          detail={`Create a new ${categoryType === 'ad-pools' ? 'ad set' : 'slate set'} to begin.`}
        />
      }
      searching={searching}
      sorting={sorting}
      filtering={filtering}
    />
  )
}

export default GroupsPoolsTable
